import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiFetch } from "./api";

const serviceTypes = ["AMC", "Repair", "Installation", "Generator", "Panels", "Supply"];
const salesStatuses = ["Open", "Quotation in Progress", "Quote Sent", "Negotiation", "Closed", "Lost"];
const clientValueUsers = new Set([
  "kandarp.g@globusafriventures.com",
  "radhey.g@globusafriventures.com",
  "paschal.a@globusafriventures.com"
]);

const emptySale = {
  site_address: "",
  service_type: "AMC",
  requirement_description: "",
  estimated_value: "",
  technical_site_visit_required: "No",
  site_visit_date: "",
  site_visit_done_by: "",
  expected_closure_date: "",
  status: "Open",
  quotation_sent: "No",
  quotation_number: "",
  quotation_amount: "",
  remarks: ""
};

function ClientSaleDetail({ user }) {
  const { id, saleId } = useParams();
  const [sale, setSale] = useState(null);
  const [draft, setDraft] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [files, setFiles] = useState({});
  const [saveStatus, setSaveStatus] = useState("");
  const canViewValues = clientValueUsers.has((user?.email || "").toLowerCase());

  const documentCategories = ["Quotation sent", "Site Survey pictures"];

  function loadSale() {
    apiFetch(`/api/sales/${saleId}`)
      .then(res => res.json())
      .then(data => {
        setSale(data);
        setDraft({ ...emptySale, ...data });
      });
  }

  function loadDocuments() {
    apiFetch(`/api/sales/${saleId}/documents`)
      .then(res => res.json())
      .then(data => setDocuments(Array.isArray(data) ? data : []));
  }

  useEffect(() => {
    loadSale();
    loadDocuments();
  }, [saleId]);

  function updateDraft(field, value) {
    setDraft(prev => ({ ...prev, [field]: value }));
  }

  function saveSale() {
    apiFetch(`/api/sales/${saleId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(draft)
    })
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          alert(data.error);
          return;
        }
        setSaveStatus("Saved");
        loadSale();
        setTimeout(() => setSaveStatus(""), 1500);
      });
  }

  function convertSale() {
    if (sale?.converted_project_id) return;
    if (!window.confirm("Convert this sale to a project?")) return;
    apiFetch(`/api/sales/${saleId}/convert`, { method: "POST" })
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          alert(data.error);
          return;
        }
        loadSale();
      });
  }

  function upload(category) {
    const file = files[category];
    if (!file) {
      alert("Select a file first");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    const params = new URLSearchParams({ category });
    apiFetch(`/api/sales/${saleId}/documents/upload?${params}`, {
      method: "POST",
      body: formData
    })
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          alert(data.error);
          return;
        }
        setFiles(prev => ({ ...prev, [category]: null }));
        loadDocuments();
      });
  }

  function openDocument(documentId) {
    apiFetch(`/api/client-documents/${documentId}/download`)
      .then(res => res.blob())
      .then(blob => window.open(window.URL.createObjectURL(blob)));
  }

  if (!sale || !draft) return <div style={page}>Loading...</div>;

  return (
    <div style={page}>
      <button onClick={() => (window.location.href = `/clients/${id}/sales`)}>Back to Sales</button>
      <h2>{sale.tracking_id || "Sale Details"}</h2>

      <div style={card}>
        <h3>Sales / RFP Information</h3>
        <SaleForm draft={draft} updateDraft={updateDraft} canViewValues={canViewValues} />
        <button onClick={saveSale}>Save Sale Details</button>
        <button
          style={{ marginLeft: "10px" }}
          disabled={Boolean(sale.converted_project_id)}
          onClick={convertSale}
        >
          {sale.converted_project_id ? "Converted to Project" : "Convert to Project"}
        </button>
        {saveStatus && <span style={successText}>{saveStatus}</span>}
      </div>

      <div style={card}>
        <h3>Sales Documents & Pictures</h3>
        <div style={documentGrid}>
          {documentCategories.map(category => (
            <div key={category} style={uploadCard}>
              <h4>{category}</h4>
              <input type="file" onChange={e => setFiles(prev => ({ ...prev, [category]: e.target.files?.[0] || null }))} />
              <button onClick={() => upload(category)}>Upload</button>
              <ul style={documentList}>
                {documents
                  .filter(doc => doc.category === category)
                  .map(doc => (
                    <li key={doc.id}>
                      <button onClick={() => openDocument(doc.id)}>Open</button>
                      <span>{doc.original_filename}</span>
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SaleForm({ draft, updateDraft, canViewValues }) {
  return (
    <div style={formGrid}>
      <Field label="Site Address">
        <input placeholder="Site address" value={draft.site_address || ""} onChange={e => updateDraft("site_address", e.target.value)} />
      </Field>
      <Field label="Service Type">
        <select value={draft.service_type || "AMC"} onChange={e => updateDraft("service_type", e.target.value)}>
          {serviceTypes.map(type => <option key={type} value={type}>{type}</option>)}
        </select>
      </Field>
      <Field label="Description of Requirement">
        <textarea placeholder="Description of requirement" value={draft.requirement_description || ""} onChange={e => updateDraft("requirement_description", e.target.value)} />
      </Field>
      {canViewValues && (
        <Field label="Estimated Value">
          <input placeholder="Estimated value" value={draft.estimated_value || ""} onChange={e => updateDraft("estimated_value", e.target.value)} />
        </Field>
      )}
      <Field label="Technical Site Visit Required">
        <select value={draft.technical_site_visit_required || "No"} onChange={e => updateDraft("technical_site_visit_required", e.target.value)}>
          <option value="No">No</option>
          <option value="Yes">Yes</option>
        </select>
      </Field>
      {draft.technical_site_visit_required === "Yes" && (
        <>
          <Field label="Site Visit Date">
            <input type="date" value={draft.site_visit_date || ""} onChange={e => updateDraft("site_visit_date", e.target.value)} />
          </Field>
          <Field label="Site Visit Done By">
            <input placeholder="Site visit done by" value={draft.site_visit_done_by || ""} onChange={e => updateDraft("site_visit_done_by", e.target.value)} />
          </Field>
        </>
      )}
      <Field label="Expected Closure Date">
        <input type="date" value={draft.expected_closure_date || ""} onChange={e => updateDraft("expected_closure_date", e.target.value)} />
      </Field>
      <Field label="Sales Status">
        <select value={draft.status || "Open"} onChange={e => updateDraft("status", e.target.value)}>
          {salesStatuses.map(status => <option key={status} value={status}>{status}</option>)}
        </select>
      </Field>
      <Field label="Quotation Sent">
        <select value={draft.quotation_sent || "No"} onChange={e => updateDraft("quotation_sent", e.target.value)}>
          <option value="No">No</option>
          <option value="Yes">Yes</option>
        </select>
      </Field>
      {draft.quotation_sent === "Yes" && (
        <>
          <Field label="Quotation Number">
            <input placeholder="Quotation number" value={draft.quotation_number || ""} onChange={e => updateDraft("quotation_number", e.target.value)} />
          </Field>
          {canViewValues && (
            <Field label="Quotation Amount">
              <input placeholder="Quotation amount" value={draft.quotation_amount || ""} onChange={e => updateDraft("quotation_amount", e.target.value)} />
            </Field>
          )}
        </>
      )}
      <Field label="Remarks">
        <textarea placeholder="Remarks" value={draft.remarks || ""} onChange={e => updateDraft("remarks", e.target.value)} />
      </Field>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={fieldLabel}>
      <span>{label}</span>
      {children}
    </label>
  );
}

const page = { padding: "20px" };
const card = { border: "1px solid #ddd", padding: "15px", margin: "20px 0", borderRadius: "8px", backgroundColor: "#f9f9f9" };
const formGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "10px", marginBottom: "12px" };
const fieldLabel = { display: "grid", gap: "4px", fontSize: "14px", fontWeight: "bold", textAlign: "left" };
const documentGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "12px" };
const uploadCard = { display: "grid", gap: "8px", border: "1px solid #ddd", borderRadius: "8px", padding: "10px", backgroundColor: "#fff" };
const documentList = { margin: 0, paddingLeft: "18px", textAlign: "left" };
const successText = { color: "green", fontWeight: "bold", marginLeft: "10px" };

export default ClientSaleDetail;
